import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  errorMessage,
  fileUploadConfig,
  Length,
  OrderCreationMasterData,
} from '@helpers/Constants';
import {
  AddonsListProps,
  MasterPackageExtens,
  MasterProduct,
  NewOrderInitiationProps,
  OrderDetailsResponse,
  OrderServiceProps,
} from '@helpers/Models';
import { CustomerOrderService } from '@services/CustomerService';
import {
  formatDate,
  resetCustomerOrderData,
} from '@services/OrderCreationService';
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TextField } from '@mui/material';
import { useToast } from '@contexts/Toast';
import { UseWarningToast } from '@contexts/WarningToast';
import { FrontendRoutes, ApiEndpoints } from '@helpers/Constants/Navigation';
import { Axios } from '@helpers/ApiService';
import { OrderConfirmationPopup } from '@components/OrderConfirmationPopup';
import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import {
  SpeciesItem,
  SelectedService,
  NewOrderSelectedProduct,
  PackagingDetail,
} from '@helpers/Models';
import { useCreditAmount } from '@contexts/CreditAmountContext';
import { useFetchStateName } from '@hooks/useFetchStateName';

const CartLayout: React.FC = () => {
  const { showToastMessage } = useToast();
  const { showWarningToast } = UseWarningToast();
  const { getStateName } = useFetchStateName();

  const location = useLocation();
  const isEditOrder = location.state?.from?.orderDetails === true;
  const orderId = location.state?.orderId;

  // Read from correct storage key based on flow
  const storageKey = isEditOrder ? 'editOrderData' : 'orderData';
  const existing = localStorage.getItem(storageKey);
  const allCustomizations = existing ? JSON.parse(existing) : {};
  const customerId = localStorage.getItem('customerId');
  const customerData = allCustomizations[customerId ?? 1];
  const speciesList = customerData?.species ?? [];
  const [customerDetails, setCustomerDetails] =
    useState<NewOrderInitiationProps>();
  const [depositStates, setDepositStates] = useState<
    Record<string, { checked: boolean; amount: string }>
  >({});
  const [advancePayment, setAdvancePayment] = useState<number>();
  const estCompletionDateRef = useRef<dayjs.Dayjs | null>(null);

  const [estCompletionDate, setEstCompletionDate] =
    useState<dayjs.Dayjs | null>(null);
  const { showConfirmationToast } = UseConfirmationToast();
  const [services, setServices] = useState<OrderServiceProps[]>([]);
  const [addons, setAddons] = useState<AddonsListProps[]>([]);

  useEffect(() => {
    const customerDetails = CustomerOrderService.getCustomerDetails();
    setCustomerDetails(customerDetails);
  }, []);

  const [openAccordions, setOpenAccordions] = useState<number[]>([]);
  const [checkinOpen, setCheckinOpen] = useState<number[]>([]);
  const [serviceOpen, setServiceOpen] = useState<number[]>([]);
  const [productOpen, setProductOpen] = useState<number[]>([]);
  const navigate = useNavigate();
  const [orderNumber, setOrderNumber] = useState<string>();
  const [showOrderConfirmModal, setShowOrderConfirmModal] = useState(false);
  const [masterProducts, setMasterProducts] = useState<MasterProduct[]>([]);
  const [masterPackage, setMasterPackage] = useState<MasterPackageExtens[]>([]);
  const [orderDetails, setOrderDetails] = useState<OrderDetailsResponse>();

  const { setHandlePlaceOrder } = useCreditAmount();

  useEffect(() => {
    estCompletionDateRef.current = estCompletionDate;
  }, [estCompletionDate]);

  // Opens all of the accordions
  useEffect(() => {
    if (speciesList.length > 0) {
      const allIndexes = speciesList.map((_: SpeciesItem, i: number) => i);
      setOpenAccordions(allIndexes);
      setCheckinOpen(allIndexes);
      setServiceOpen(allIndexes);
      setProductOpen(allIndexes);
    }
  }, []);

  const totalDeposits = (() => {
    // Deposits from newly added services
    const checkedDeposits = Object.values(depositStates)
      .filter((d) => d.checked)
      .reduce((sum, d) => sum + (Number(d.amount) || 0), 0);

    if (!isEditOrder) return checkedDeposits;

    // Add existing service deposits from editOrderData
    const existingDeposits = speciesList.reduce((sum: number, species: any) => {
      return (
        sum +
        (species.services?.reduce((sSum: number, service: any) => {
          // Only count existing services
          if (service.serviceId && service.depositAmount > 0) {
            return sSum + service.depositAmount;
          }
          return sSum;
        }, 0) ?? 0)
      );
    }, 0);

    return checkedDeposits + existingDeposits;
  })();

  // Total = advance payment + all deposits
  const totalAmountToBeCollected =
    (Number(advancePayment) || 0) + totalDeposits;

  /**
   * Initial component mounting hook.
   * * This effect performs the following synchronization tasks:
   * 1. Hydrates customer details and IDs from the CustomerOrderService.
   * 2. Retrieves existing order progress from localStorage (toggling between 'editOrderData' and 'orderData').
   * 3. Maps service statuses into a lookup dictionary (statusMap).
   * 4. Restores estimated completion dates using dayjs.
   * 5. Triggers a series of API fetches (Services, Addons, Products, Packages) if a species selection is present.
   * * @listens componentDidMount
   */
  useEffect(() => {
    const customerDetails = CustomerOrderService.getCustomerDetails();
    setCustomerDetails(customerDetails);
    const customerId = CustomerOrderService.getCustomerId();

    const storageKey = isEditOrder ? 'editOrderData' : 'orderData';
    const raw = localStorage.getItem(storageKey);
    if (!raw) return;

    const parsedOrderData = JSON.parse(raw);
    const currentCustomerOrderData = parsedOrderData[customerId];
    if (!currentCustomerOrderData) return;

    if (currentCustomerOrderData.estCompletionDate) {
      setEstCompletionDate(dayjs(currentCustomerOrderData.estCompletionDate));
    } else if (isEditOrder) {
      setEstCompletionDate(dayjs(orderDetails?.orderInfo.estCompletionDate));
    }

    if (currentCustomerOrderData.speciesId) {
      fetchServiceMaster(currentCustomerOrderData.speciesId);
      fetchAddons();
      fetchMasterProducts();
      fetchMasterPackage();
      fetchEstimationCompletionDate();
      setAdvancePayment(currentCustomerOrderData.advancePayment);
    }
  }, []);

  useEffect(() => {
    if (isEditOrder) {
      setAdvancePayment(orderDetails?.orderInfo.advancePayment);
    }
  }, [orderDetails]);

  useEffect(() => {
    if (orderId) {
      fetchOrderDetails();
    }
  }, [orderId]);

  /**
   * Fetches full order details from the server using the current order ID.
   * * This function sends a POST request to the ORDER_DETAILS endpoint.
   * On success, it updates the `orderDetails` state with the retrieved data.
   * On failure, it triggers a toast notification to alert the user.
   * * @async
   * @returns {Promise<void>} Resolves when the state has been updated or an error has been handled.
   */
  const fetchOrderDetails = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.ORDER_DETAILS, {
        id: orderId,
      });
      setOrderDetails(response.data.data);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches the list of available addons from the server.
   *
   * This function makes an API call to retrieve all addons and updates the `addons` state
   * with the fetched data. It uses the `showToastMessage` function to display an error
   * message if the fetch operation fails.
   *
   * @returns {Promise<void>} A promise that resolves when the addons have been fetched.
   */
  const fetchAddons = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_ADDONS, {});
      setAddons(response.data.data.addOns);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches the master product list from the species master data repository.
   * * This function specifically requests `PRODUCT_MASTER_DATA` from a generic master data
   * endpoint. It populates the `masterProducts` state which is used to drive
   * product selection dropdowns or lists in the UI.
   * * @async
   * @returns {Promise<void>} Resolves after the state update or error handling is complete.
   */
  const fetchMasterProducts = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType: OrderCreationMasterData.PRODUCT_MASTER_DATA,
      });
      setMasterProducts(response.data.data.masterData);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches the master list of packaging options from the species master data repository.
   * * This function requests `PACKAGE_MASTER_DATA` from a generic master data endpoint.
   * It extracts the nested `packageType` array and populates the `masterPackage` state.
   * * @async
   * @returns {Promise<void>} Resolves when the package state has been updated or an error handled.
   */
  const fetchMasterPackage = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType: OrderCreationMasterData.PACKAGE_MASTER_DATA,
      });
      const masterData = response.data.data.masterData.packageType;
      setMasterPackage(masterData);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches the estimated completion date from the server.
   *
   * This function makes an API call to retrieve the estimated completion date
   * and updates the `estCompletionDate` state with the fetched data. It uses
   * the `showToastMessage` function to display an error message if the fetch
   * operation fails.
   *
   * @returns {Promise<void>} A promise that resolves when the estimated completion date has been fetched.
   */
  const fetchEstimationCompletionDate = async (): Promise<void> => {
    const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
      masterDataType: OrderCreationMasterData.ORDER_ESTIMATION_DATE,
    });
    setEstCompletionDate(dayjs(response.data.data.masterData));
  };

  /**
   * Fetches the service master data for a specific species.
   *
   * This function makes an API call to retrieve the service master data for a given species ID
   * and updates the `services` state with the fetched data. It uses the `showToastMessage` function
   * to display an error message if the fetch operation fails.
   *
   * @param speciesId - The ID of the species to fetch the service master data for.
   * @returns {Promise<void>} A promise that resolves when the service master data has been fetched.
   */
  const fetchServiceMaster = async (speciesId: number): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        speciesId: speciesId,
        masterDataType: OrderCreationMasterData.SERVICE_MASTER_DATA,
      });
      setServices(response.data.data.masterData.services);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Toggles the checked state of a deposit.
   *
   * This function updates the `depositStates` state by toggling the `checked` property
   * for a specific deposit identified by its `key`. It also initializes the `amount`
   * property with an empty string if it doesn't exist.
   *
   * @param key - The key identifying the deposit to toggle.
   */
  const toggleDeposit = (key: string) => {
    setDepositStates((prev) => ({
      ...prev,
      [key]: { checked: !prev[key]?.checked, amount: prev[key]?.amount ?? '' },
    }));
  };

  /**
   * Sets the amount for a specific deposit.
   *
   * This function updates the `depositStates` state by setting the `amount` property
   * for a specific deposit identified by its `key`. It preserves the current `checked` state
   * of the deposit while updating the amount.
   *
   * @param key - The key identifying the deposit to update.
   * @param amount - The new amount to set for the deposit.
   */
  const setDepositAmount = (key: string, amount: string) => {
    const sanitizedAmount = amount
      .replace(/[^0-9.]/g, '')
      .replace(/(\..*)\./g, '$1')
      .replace(/^(\d*\.\d{0,2}).*$/, '$1');

    setDepositStates((prev) => ({
      ...prev,
      [key]: { ...prev[key], amount: sanitizedAmount },
    }));
  };

  const handleDepositKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
    ];

    if (allowedKeys.includes(e.key)) return;

    if (e.key === '.') {
      const currentValue = e.currentTarget.value;
      if (currentValue.includes('.')) {
        e.preventDefault();
      }
      return;
    }

    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const validateDepositAmounts = (): boolean => {
    for (const deposit of Object.values(depositStates)) {
      if (!deposit.checked) continue;

      const trimmedAmount = deposit.amount.trim();
      const numericAmount = Number(trimmedAmount);
      if (trimmedAmount === '' || Number.isNaN(numericAmount)) {
        showToastMessage(
          'Please enter a valid numeric deposit amount before placing the order.',
          'error',
        );
        return false;
      }

      if (numericAmount < 0) {
        showToastMessage('Deposit amount cannot be negative.', 'error');
        return false;
      }
    }

    return true;
  };

  /**
   * Handles the editing of a species customization.
   *
   * This function sets the `currentCustomizingId` in the local storage to the provided
   * `speciesIndex` and navigates the user to the species customization page.
   *
   * @param speciesIndex - The index of the species to edit.
   */
  const handleEdit = (speciesIndex: number) => {
    localStorage.setItem('currentCustomizingId', String(speciesIndex));
    navigate('/create-order/species/customize', {
      state: isEditOrder
        ? {
          from: { orderDetails: true },
          orderId: orderDetails?.orderInfo?.orderId,
        }
        : undefined,
    });
  };

  /**
   * Handles the completion of an order.
   *
   * This function validates the order, calculates the total amount to be collected,
   * and displays a confirmation modal. If the estimated completion date is not set,
   * it shows an error message and returns.
   *
   * @returns {Promise<void>} A promise that resolves when the order completion process is initiated.
   */
  const handleCompleteOrder = async (): Promise<void> => {
    // const finalEstCompletionDate = estCompletionDate?.format('YYYY-MM-DD');
    const finalEstCompletionDate =
      estCompletionDateRef.current?.format('YYYY-MM-DD');
    console.log('finalEstCompletionDate', finalEstCompletionDate);

    if (!validateDepositAmounts()) return;

    if (isEditOrder) {
      // Edit order flow
      const raw = localStorage.getItem('editOrderData');
      if (!raw) return;
      const all = JSON.parse(raw);
      const customerDetails = CustomerOrderService.getCustomerDetails();
      const currentCustomerOrderData = all[customerDetails?.id ?? 1];

      const updatedSpecies = currentCustomerOrderData.species.map(
        (species: SpeciesItem, speciesIdx: number) => {
          const updatedServices =
            species.services?.map((service: any, sIdx: number) => {
              const key = `${speciesIdx}-${sIdx}`;
              const deposit = depositStates[key];
              return {
                ...service,
                ...(deposit?.checked
                  ? { depositAmount: Number(deposit.amount) }
                  : {}),
              };
            }) ?? [];

          return {
            ...species,
            services: updatedServices,
          };
        },
      );

      const payload = {
        orderId: orderId,
        speciesId: currentCustomerOrderData.speciesId,
        customerId: currentCustomerOrderData.customerId,
        estCompletionDate: finalEstCompletionDate,
        advancePayment: advancePayment ?? null,
        quantity: currentCustomerOrderData.quantity,
        speciesName: currentCustomerOrderData.speciesName,
        species: updatedSpecies.map((species: any) => {
          const { creditAmount, isEdited, ...restSpecies } = species;

          return {
            ...restSpecies,
            speciesId: species.speciesId,

            services:
              species.services?.map((service: any) => {
                const { colorId, ...rest } = service;

                return {
                  ...rest,
                  color: colorId,
                  serviceId: service.serviceId,
                  butcherServiceId: service.butcherServiceId,
                };
              }) ?? [],

            products:
              species.products?.map((product: any) => ({
                ...product,
                productId: product.productId,
                butcherProductId: product.butcherProductId,
              })) ?? [],

            deletedServiceIds: species.deletedServiceIds ?? [],
            deletedProductIds: species.deletedProductIds ?? [],
          };
        }),
      };

      try {
        const response = await Axios.post(ApiEndpoints.EDIT_ORDER, payload);
        const orderId = response.data.data.order.id;
        setOrderNumber(response.data.data.orderNumber);
        showToastMessage('Order edited successfully', 'success');
        localStorage.removeItem('editOrderData');
        navigate(FrontendRoutes.ORDER_DETAILS, { state: { orderId } });
        window.location.reload();
      } catch (error) {
        showToastMessage(errorMessage.ERROR_CREATING_ORDER, 'error');
      }
    } else {
      // Create order flow
      // Check whether the estimation date is empty while creating the order
      if (finalEstCompletionDate == null) {
        showToastMessage(errorMessage.ORDER_ESTIMATION_DATE_REQUIRED, 'error');
        return;
      }
      const parsedOrderData = CustomerOrderService.getParsedOrderData();
      const customerDetails = CustomerOrderService.getCustomerDetails();
      const currentCustomerOrderData =
        parsedOrderData[customerDetails?.id ?? 1];

      const updatedSpecies = currentCustomerOrderData.species.map(
        (species: SpeciesItem, speciesIdx: number) => {
          const updatedServices =
            species.services?.map((service: SelectedService, sIdx: number) => {
              const key = `${speciesIdx}-${sIdx}`;
              const deposit = depositStates[key];
              return {
                ...service,
                color: service.colorId,
                ...(deposit?.checked
                  ? { depositAmount: Number(deposit.amount) }
                  : {}),
              };
            }) ?? [];
          return { ...species, services: updatedServices };
        },
      );

      const payload = {
        ...currentCustomerOrderData,
        species: updatedSpecies,
        estCompletionDate: finalEstCompletionDate,
        advancePayment: advancePayment,
      };

      const updatedOrderData = {
        ...parsedOrderData,
        [customerDetails?.id ?? 1]: {
          ...currentCustomerOrderData,
          species: updatedSpecies,
          advancePayment: advancePayment,
          estCompletionDate: finalEstCompletionDate,
        },
      };

      CustomerOrderService.setParsedOrderData(updatedOrderData);

      const { species: speciesList, quantity } = currentCustomerOrderData;
      if (!speciesList || speciesList.length !== quantity) {
        showWarningToast(
          errorMessage.ORDER_SPECIES_CUSTOMIZATION_FAILURE,
          'Customization Incomplete',
          () => navigate(FrontendRoutes.CREATE_ORDER_SPECIES_CUSTOMISATION),
        );
        return;
      }

      try {
        const response = await Axios.post(ApiEndpoints.CREATE_ORDER, payload);
        const orderId = response.data.data.orderId;
        setOrderNumber(response.data.data.orderNumber);
        setShowOrderConfirmModal(true);
        resetCustomerOrderData();
        setTimeout(() => {
          navigate(FrontendRoutes.ORDER_DETAILS, { state: { orderId } });
          setShowOrderConfirmModal(false);
          window.location.reload();
        }, fileUploadConfig.NETWORK_DELAY_MS);
      } catch (error) {
        showToastMessage(errorMessage.ERROR_CREATING_ORDER, 'error');
      }
    }
  };

  /**
   * Handles the order placement flow.
   *
   * - If the order is a new order (not in edit mode) and there is a pending amount
   *   to be collected, it prompts the user with a confirmation toast to ensure
   *   payment is collected before completing the order.
   * - If no payment is pending or the order is being edited, it directly proceeds
   *   to complete the order.
   *
   * Dependencies:
   * @dependency isEditOrder - Indicates whether the order is being edited.
   * @dependency totalAmountToBeCollected - The remaining amount to be collected from the customer.
   *
   * Actions:
   * - Triggers `showConfirmationToast` when payment collection is required.
   * - Calls `handleCompleteOrder` directly when no confirmation is needed.
   */
  const handlePlaceOrder = useCallback(() => {
    if (!isEditOrder && totalAmountToBeCollected != 0) {
      showConfirmationToast(
        `To complete this order, please make sure to collect $${totalAmountToBeCollected} from the customer.`,
        'Collect Payment',
        'info',
        handleCompleteOrder,
        'Mark as Paid',
        () => { }, // Cancel - stay
        'Cancel',
      );
    } else {
      handleCompleteOrder();
    }
  }, [isEditOrder, totalAmountToBeCollected]);

  /**
   * Registers the latest `handlePlaceOrder` function in the parent or external state.
   *
   * - Updates the reference whenever `handlePlaceOrder` changes.
   * - Ensures that the consuming component always invokes the most up-to-date
   *   version of the order placement handler.
   *
   * Notes:
   * - Uses optional chaining to safely call `setHandlePlaceOrder` if it is provided.
   * - Wraps `handlePlaceOrder` in a function to preserve its reference when storing.
   *
   * Dependencies:
   * @dependency handlePlaceOrder - Triggers re-registration when updated.
   */
  useEffect(() => {
    setHandlePlaceOrder?.(() => handlePlaceOrder);
  }, [handlePlaceOrder]);

  return (
    <div>
      <div className="p-2 mt-2">
        {/* Order Confirm Modal */}
        {showOrderConfirmModal && (
          <OrderConfirmationPopup
            orderNumber={orderNumber}
            visible={showOrderConfirmModal}
            onClose={() => setShowOrderConfirmModal(false)}
          />
        )}
        {speciesList.map((species: SpeciesItem, index: number) => (
          <div key={index} className="border rounded-md shadow-sm mb-3">
            <div
              onClick={() =>
                setOpenAccordions((prev) =>
                  prev.includes(index)
                    ? prev.filter((i) => i !== index)
                    : [...prev, index],
                )
              }
              className="w-full flex justify-between items-center px-4 py-3 font-semibold text-lg cursor-pointer"
            >
              <span>
                {species.speciesName}
                {species.checkIn.feet && species.checkIn.inches && (
                  <>
                    {''} ({species.checkIn.feet} {Length.FEET}{' '}
                    {species.checkIn.inches} {Length.INCHES})
                  </>
                )}
              </span>
              <div className="flex items-center gap-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(index);
                  }}
                  type="button"
                  className="flex items-center gap-2"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1.55556 12.4444H2.66389L10.2667 4.84167L9.15833 3.73333L1.55556 11.3361V12.4444ZM0 14V10.6944L10.2667 0.447222C10.4222 0.30463 10.594 0.194444 10.7819 0.116667C10.9699 0.0388889 11.1676 0 11.375 0C11.5824 0 11.7833 0.0388889 11.9778 0.116667C12.1722 0.194444 12.3407 0.311111 12.4833 0.466667L13.5528 1.55556C13.7083 1.69815 13.8218 1.86667 13.8931 2.06111C13.9644 2.25556 14 2.45 14 2.64444C14 2.85185 13.9644 3.04954 13.8931 3.2375C13.8218 3.42546 13.7083 3.59722 13.5528 3.75278L3.30556 14H0ZM9.70278 4.29722L9.15833 3.73333L10.2667 4.84167L9.70278 4.29722Z"
                      fill="#000A16"
                    />
                  </svg>
                  <span>Edit</span>
                </button>

                <FontAwesomeIcon
                  className="text-[#000A16]"
                  icon={faChevronDown}
                />
              </div>
            </div>
            {openAccordions.includes(index) && (
              <div className="p-4">
                <div className="rounded-md overflow-hidden bg-[#EAF1F866] mt-2 border border-[#2A6FBA1A]">
                  {/* Accordion Header */}
                  <button
                    onClick={() =>
                      setCheckinOpen((prev) =>
                        prev.includes(index)
                          ? prev.filter((i) => i !== index)
                          : [...prev, index],
                      )
                    }
                    type="button"
                    className="w-full flex justify-between items-center p-2 font-semibold text-lg outline-none"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#DFEEFF]">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10C0 8.61667 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.7875C7.31667 0.2625 8.61667 0 10 0C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20ZM10 18C12.2333 18 14.125 17.225 15.675 15.675C17.225 14.125 18 12.2333 18 10C18 7.76667 17.225 5.875 15.675 4.325C14.125 2.775 12.2333 2 10 2C7.76667 2 5.875 2.775 4.325 4.325C2.775 5.875 2 7.76667 2 10C2 12.2333 2.775 14.125 4.325 15.675C5.875 17.225 7.76667 18 10 18ZM6 14V10.925L11.525 5.425C11.675 5.275 11.8417 5.16667 12.025 5.1C12.2083 5.03333 12.3917 5 12.575 5C12.775 5 12.9667 5.0375 13.15 5.1125C13.3333 5.1875 13.5 5.3 13.65 5.45L14.575 6.375C14.7083 6.525 14.8125 6.69167 14.8875 6.875C14.9625 7.05833 15 7.24167 15 7.425C15 7.60833 14.9667 7.79583 14.9 7.9875C14.8333 8.17917 14.725 8.35 14.575 8.5L9.075 14H6ZM7.5 12.5H8.45L11.475 9.45L11.025 8.975L10.55 8.525L7.5 11.55V12.5ZM11.025 8.975L10.55 8.525L11.475 9.45L11.025 8.975Z"
                            fill="#2A6FBA"
                          />
                        </svg>
                      </div>

                      <span>Check - In </span>
                    </div>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className={`text-[#2A6FBA] transition-transform duration-300 ${checkinOpen ? 'rotate-180' : ''
                        }`}
                    />
                  </button>
                  {checkinOpen.includes(index) && (
                    <div className="p-3 space-y-3 bg-white">
                      {/* Info Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-2">
                        {/* License Section */}
                        {species.checkIn.licenseNumber && (
                          <div className="bg-[#F8F8F8] p-4 rounded-sm">
                            <h4 className="text-[#1C1B1F] text-lg mb-1">
                              License:
                            </h4>

                            <div className="grid grid-cols-2 gap-y-2 text-[#1C1B1F] font-semibold">
                              <span>
                                {customerDetails?.firstName}{' '}
                                {customerDetails?.lastName}
                              </span>
                              {/* If edit order, show state name, else get the state name */}
                              {isEditOrder ? (
                                <span> {species.checkIn.stateName} </span>
                              ) : (
                                <span>
                                  {getStateName(
                                    Number(species.checkIn.harvestStateId),
                                  )}
                                </span>
                              )}
                              <span className="break-words">
                                {species.checkIn.licenseNumber}
                              </span>
                              <span>
                                {' '}
                                {formatDate(
                                  customerDetails?.HuntingLicences?.find(
                                    (license) =>
                                      license.id ===
                                      species.checkIn.huntingLicenceId,
                                  )?.expiryDate || '',
                                )}
                              </span>
                            </div>
                          </div>
                        )}
                        {/* Numbers Section */}
                        <div className="bg-[#F8F8F8] text-[#1C1B1F] p-4 rounded-sm space-y-3">
                          <div className="flex flex-col">
                            <span>Permit Number:</span>
                            <span className="font-semibold break-words">
                              {species.checkIn?.permitNumber ?? '-'}
                            </span>
                          </div>
                          <div className="flex flex-col">
                            <span>CITES Number:</span>
                            <span className="font-semibold break-words">
                              {species.checkIn?.citesNumber ?? '-'}
                            </span>
                          </div>
                          <div className="flex flex-col">
                            <span>Confirmation Number:</span>
                            <span className="font-semibold break-words">
                              {species.checkIn?.conformationNumber ?? '-'}
                            </span>
                          </div>
                        </div>

                        {/* Measurements Section */}
                        <div className="bg-[#F8F8F8] text-[#1C1B1F] p-4 rounded-sm space-y-3">
                          <div className="flex gap-4">
                            <span>Length:</span>
                            <span className="font-semibold">
                              {species.checkIn?.feet
                                ? `${species.checkIn.feet} Feet ${species.checkIn.inches || 0} Inches`
                                : '-'}
                            </span>
                          </div>
                          <div className="flex gap-4">
                            <span>Weight:</span>
                            <span className="font-semibold">
                              {species.checkIn?.weight || '-'}{' '}
                              {species.checkIn?.weight ? 'Lbs' : ''}
                            </span>
                          </div>
                        </div>

                        {/* Notes Section */}
                        <div className="bg-[#F8F8F8] text-[#1C1B1F] p-4 rounded-sm">
                          <h4 className="mb-1">Check - In Notes:</h4>
                          <p className="text-sm">
                            {species.checkIn?.notes ?? '-'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* Service accordion */}
                <div className="rounded-md overflow-hidden border-2 border-[#9F57D41A] bg-white mt-2">
                  {/* Header */}
                  <button
                    onClick={() =>
                      setServiceOpen((prev) =>
                        prev.includes(index)
                          ? prev.filter((i) => i !== index)
                          : [...prev, index],
                      )
                    }
                    type="button"
                    className="w-full flex justify-between items-center p-2 mt-1 font-semibold text-lg outline-none bg-[#F5EEFB66]"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F3E8FF]">
                        {' '}
                        <svg
                          width="20"
                          height="16"
                          viewBox="0 0 20 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M0 16V6C0 5.45 0.195833 4.97917 0.5875 4.5875C0.979167 4.19583 1.45 4 2 4H5V2C5 1.45 5.19583 0.979167 5.5875 0.5875C5.97917 0.195833 6.45 0 7 0H13C13.55 0 14.0208 0.195833 14.4125 0.5875C14.8042 0.979167 15 1.45 15 2V4H18C18.55 4 19.0208 4.19583 19.4125 4.5875C19.8042 4.97917 20 5.45 20 6V16H0ZM6 11V12H4V11H2V14H18V11H16V12H14V11H6ZM2 6V9H4V8H6V9H14V8H16V9H18V6H2ZM7 4H13V2H7V4Z"
                            fill="#9F57D4"
                          />
                        </svg>
                      </div>
                      <span>Services</span>
                    </div>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className={`text-[#9F57D4] transition-transform duration-300 ${serviceOpen ? 'rotate-180' : ''
                        }`}
                    />
                  </button>

                  {/* Accordion Content */}
                  {serviceOpen.includes(index) && (
                    <div className="p-3">
                      {(species.services?.length ?? 0) > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                          {/* Service Card */}
                          {species.services?.map(
                            (service: SelectedService, sIdx: number) => {
                              const key = `${index}-${sIdx}`;
                              const deposit = depositStates[key];
                              const isCompleted = (service.status ?? 0) > 0;
                              const isExistingService =
                                isEditOrder && service.serviceId !== null;

                              const serviceDetails = services?.find(
                                (s) =>
                                  s.id ===
                                  (isEditOrder
                                    ? service.butcherServiceId
                                    : service.serviceId),
                              );

                              return (
                                <div
                                  key={sIdx}
                                  className={`bg-[#F8F8F8] rounded-md p-3 shadow-sm`}
                                >
                                  {isCompleted && (
                                    <div className="inline-block mb-1">
                                      <div className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-badge-green-bg text-badge-green border border-badge-green w-fit">
                                        <svg
                                          width="12"
                                          height="12"
                                          viewBox="0 0 12 12"
                                          fill="none"
                                        >
                                          <circle
                                            cx="6"
                                            cy="6"
                                            r="6"
                                            fill="#16a34a"
                                          />
                                          <path
                                            d="M3 6l2 2 4-4"
                                            stroke="white"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                        Completed
                                      </div>
                                    </div>
                                  )}

                                  <div
                                    className={`${isCompleted ? 'opacity-50' : ''}`}
                                  >
                                    <div className="flex justify-between font-semibold">
                                      <span>
                                        {' '}
                                        {serviceDetails?.name ??
                                          `Service ${service.serviceId}`}
                                      </span>
                                      <div className="flex items-center gap-2">
                                        <span>${serviceDetails?.rate}</span>
                                      </div>
                                    </div>

                                    <p className="text-sm text-gray-500 mt-1">
                                      {
                                        'Total Price to be calculated after processing'
                                      }
                                    </p>

                                    {service.color && (
                                      <span className="text-[#797979] text-sm">
                                        {service.color}
                                      </span>
                                    )}

                                    {isExistingService &&
                                      Number(service.depositAmount) > 0 && (
                                        <div className="flex justify-between items-center mt-7 pt-2 bg-white border-t border-gray-100">
                                          <span className="text-[#6C7689]">
                                            Deposit
                                          </span>
                                          <span className="font-bold">
                                            ${service.depositAmount}
                                          </span>
                                        </div>
                                      )}
                                    {!isCompleted &&
                                      !isExistingService &&
                                      (!service.depositAmount ||
                                        Number(service.depositAmount) === 0) && (
                                        <div className="collect-deposit">
                                          <div className="flex justify-between items-center mt-3 bg-white p-2">
                                            <span className="text-sm">
                                              Collect Deposit
                                            </span>
                                            <input
                                              type="checkbox"
                                              checked={deposit?.checked ?? false}
                                              onChange={() => toggleDeposit(key)}
                                              className="accent-primary"
                                              disabled={isExistingService}
                                            />
                                          </div>
                                          {deposit?.checked && (
                                            <div className="flex justify-between items-center bg-white p-2">
                                              <span className="text-sm font-semibold">
                                                Deposit
                                              </span>
                                              <div className="relative w-32">
                                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                                                  $
                                                </span>
                                                <input
                                                  type="text"
                                                  inputMode="decimal"
                                                  value={deposit?.amount ?? ''}
                                                  onKeyDown={handleDepositKeyDown}
                                                  onChange={(e) =>
                                                    setDepositAmount(
                                                      key,
                                                      e.target.value,
                                                    )
                                                  }
                                                  disabled={isExistingService}
                                                  className="border border-gray-300 rounded-md pl-5 pr-2 py-1 w-full text-sm text-right focus:outline-primary"
                                                />
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-[#6C7689] px-1 py-2">
                          No service is added to the species
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Product accordion */}
                <div className="rounded-md overflow-hidden bg-[#F9E6DF4D] mt-2 border border-[#AD67491A]">
                  {/* Accordion Header */}
                  <button
                    onClick={() =>
                      setProductOpen((prev) =>
                        prev.includes(index)
                          ? prev.filter((i) => i !== index)
                          : [...prev, index],
                      )
                    }
                    type="button"
                    className="w-full flex justify-between items-center p-2 font-semibold text-lg outline-none"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F9E6DF]">
                        <svg
                          width="20"
                          height="16"
                          viewBox="0 0 21 18"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M19.0469 8.05V16C19.0469 16.55 18.8511 17.0208 18.4594 17.4125C18.0678 17.8042 17.5969 18 17.0469 18H3.04694C2.49694 18 2.02611 17.8042 1.63444 17.4125C1.24277 17.0208 1.04694 16.55 1.04694 16V8.05C0.663606 7.7 0.367772 7.25 0.159439 6.7C-0.0488946 6.15 -0.0530612 5.55 0.146939 4.9L1.19694 1.5C1.33027 1.06667 1.56777 0.708333 1.90944 0.425C2.25111 0.141667 2.64694 0 3.09694 0H16.9969C17.4469 0 17.8386 0.1375 18.1719 0.4125C18.5053 0.6875 18.7469 1.05 18.8969 1.5L19.9469 4.9C20.1469 5.55 20.1428 6.14167 19.9344 6.675C19.7261 7.20833 19.4303 7.66667 19.0469 8.05ZM12.2469 7C12.6969 7 13.0386 6.84583 13.2719 6.5375C13.5053 6.22917 13.5969 5.88333 13.5469 5.5L12.9969 2H11.0469V5.7C11.0469 6.05 11.1636 6.35417 11.3969 6.6125C11.6303 6.87083 11.9136 7 12.2469 7ZM7.74694 7C8.13027 7 8.44277 6.87083 8.68444 6.6125C8.92611 6.35417 9.04694 6.05 9.04694 5.7V2H7.09694L6.54694 5.5C6.48027 5.9 6.56777 6.25 6.80944 6.55C7.05111 6.85 7.36361 7 7.74694 7ZM3.29694 7C3.59694 7 3.85944 6.89167 4.08444 6.675C4.30944 6.45833 4.44694 6.18333 4.49694 5.85L5.04694 2H3.09694L2.09694 5.35C1.99694 5.68333 2.05111 6.04167 2.25944 6.425C2.46777 6.80833 2.81361 7 3.29694 7ZM16.7969 7C17.2803 7 17.6303 6.80833 17.8469 6.425C18.0636 6.04167 18.1136 5.68333 17.9969 5.35L16.9469 2H15.0469L15.5969 5.85C15.6469 6.18333 15.7844 6.45833 16.0094 6.675C16.2344 6.89167 16.4969 7 16.7969 7ZM3.04694 16H17.0469V8.95C16.9636 8.98333 16.9094 9 16.8844 9H16.7969C16.3469 9 15.9511 8.925 15.6094 8.775C15.2678 8.625 14.9303 8.38333 14.5969 8.05C14.2969 8.35 13.9553 8.58333 13.5719 8.75C13.1886 8.91667 12.7803 9 12.3469 9C11.8969 9 11.4761 8.91667 11.0844 8.75C10.6928 8.58333 10.3469 8.35 10.0469 8.05C9.76361 8.35 9.43444 8.58333 9.05944 8.75C8.68444 8.91667 8.28027 9 7.84694 9C7.36361 9 6.92611 8.91667 6.53444 8.75C6.14277 8.58333 5.79694 8.35 5.49694 8.05C5.14694 8.4 4.80111 8.64583 4.45944 8.7875C4.11777 8.92917 3.73027 9 3.29694 9H3.18444C3.14277 9 3.09694 8.98333 3.04694 8.95V16Z"
                            fill="#AD6749"
                          />
                        </svg>
                      </div>

                      <span>Products</span>
                    </div>
                    <FontAwesomeIcon
                      icon={faChevronUp}
                      className={`text-[#AD6749] transition-transform duration-300 ${productOpen ? 'rotate-180' : ''
                        }`}
                    />
                  </button>
                  {productOpen.includes(index) && (
                    (species.products?.length ?? 0) > 0 ? (
                      <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 bg-white">
                        {species.products?.map(
                          (product: NewOrderSelectedProduct, pIdx: number) => {
                            const isCompleted = (product.status ?? 0) > 0;
                            // const isCompleted = (productStatusMap[product.productId ?? -1] ?? 0) > 0;

                            return (
                              <div
                                key={pIdx}
                                className="border border-[#0000001A] rounded-lg p-2 overflow-hidden"
                              >
                                {/* Card Header */}
                                <div className="bg-[#FDF5F2] p-3 border-b border-gray-100 flex items-center justify-between">
                                  <h3
                                    className={`font-semibold text-[#1C1B1F] ${isCompleted ? 'opacity-50' : ''}`}
                                  >
                                    {masterProducts.find(
                                      (p) =>
                                        p.id ===
                                        (isEditOrder
                                          ? product.butcherProductId
                                          : product.productId),
                                    )?.name ?? '-'}
                                  </h3>

                                  {/* Completed badge */}
                                  {isCompleted && (
                                    <div className="flex items-center gap-1 bg-badge-green-bg text-badge-green border border-badge-green text-xs font-semibold px-2 py-1 rounded-full">
                                      <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 12 12"
                                        fill="none"
                                      >
                                        <circle
                                          cx="6"
                                          cy="6"
                                          r="6"
                                          fill="#16a34a"
                                        />
                                        <path
                                          d="M3 6l2 2 4-4"
                                          stroke="white"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                      Completed
                                    </div>
                                  )}
                                </div>

                                {/* Card Body */}
                                <div
                                  className={`${isCompleted ? 'opacity-50' : ''}`}
                                >
                                  <div className="p-2 space-y-2 text-sm">
                                    <div className="flex gap-2">
                                      <span className="text-[#43474E] shrink-0">Quantity:</span>
                                      <span className="font-semibold text-[#1C1B1F]">
                                        {product.quantity}
                                        {masterProducts.find(
                                          (p) =>
                                            p.id ===
                                            (isEditOrder
                                              ? product.butcherProductId
                                              : product.productId),
                                        )?.productType == 2 ? ' Qty' : ' Lbs'}
                                      </span>
                                    </div>
                                    {product.fatPercent !== 0 && (
                                      <div className="flex gap-2">
                                        <span className="text-[#43474E] shrink-0">Fat:</span>
                                        <span className="font-semibold text-[#1C1B1F]">{product.fatPercent}%</span>
                                      </div>
                                    )}
                                    {/* Add-Ons */}
                                    {product.addOnIds?.length > 0 && (
                                      <>
                                        <div className="flex gap-2">
                                          <span className="text-[#43474E] shrink-0">
                                            Add-Ons:
                                          </span>
                                          <span className="font-semibold text-[#1C1B1F]">
                                            {product.addOnIds
                                              .map(
                                                (id: number) =>
                                                  addons?.find(
                                                    (addon) => addon.id === id,
                                                  )?.name ?? `Addon ${id}`,
                                              )
                                              .join(', ')}
                                          </span>
                                        </div>
                                        <hr className="border-gray-100" />
                                      </>
                                    )}
                                    {/* Packaging */}
                                    {product.packaging &&
                                      product.packaging?.length > 0 && (
                                        <>
                                          <div className="space-y-1 text-sm text-[#43474E]">
                                            <p className="font-semibold">
                                              Packaging:
                                            </p>
                                            {product.packaging.map(
                                              (
                                                pkg: PackagingDetail,
                                                pkgIdx: number,
                                              ) => (
                                                <div
                                                  key={pkgIdx}
                                                  className="flex items-center"
                                                >
                                                  <span>
                                                    {
                                                      masterPackage.find(
                                                        (p) =>
                                                          p.id ===
                                                          pkg.productPackageTypeId,
                                                      )?.packageType
                                                    }
                                                  </span>
                                                  <span className="mx-2">-</span>
                                                  <span>
                                                    Pkg Split:{' '}
                                                    <span className="font-bold">
                                                      {pkg.percentage}%
                                                    </span>
                                                  </span>
                                                </div>
                                              ),
                                            )}
                                          </div>
                                          <hr className="border-gray-100" />
                                        </>
                                      )}

                                    {/* Notes */}
                                    {product.packagingNotes && (
                                      <div>
                                        <p className="font-semibold text-[#1C1B1F]">
                                          Packaging Notes:
                                        </p>
                                        <p className="mt-1 text-[#1C1B1F]">
                                          {product.packagingNotes}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          },
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-[#6C7689] px-4 py-3 bg-white">
                        No product is added to the species
                      </p>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="p-2">
        <div className="sticky bottom-0 z-40 bg-[#E0EBEA] border-t rounded-md">
          <div className="px-3 py-3 w-full">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-2">
              <div className="flex items-center gap-2">
                {isEditOrder ? (
                  <span className="text-sm text-gray-700">
                    Advance Payment:{' '}
                    <span className="font-bold text-gray-900">
                      ${orderDetails?.orderInfo.advancePayment}
                    </span>
                  </span>
                ) : (
                  <>
                    <span className="text-gray-600 text-xs md:text-sm whitespace-nowrap">
                      Advance Payment:
                    </span>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-600 font-bold text-sm">
                        $
                      </span>
                      <input
                        type="number"
                        value={advancePayment}
                        onChange={(e) =>
                          setAdvancePayment(Number(e.target.value))
                        }
                        className="w-[90px] md:w-[80px] xl:w-[140px] h-8 pl-5 pr-2 rounded-md border border-gray-300 bg-white text-center font-bold text-sm focus:outline-primary"
                        placeholder="0"
                        min={0}
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="h-4 w-px bg-gray-400 opacity-40" />

              <div className="flex items-center gap-1">
                <span className="text-gray-600 text-xs md:text-sm whitespace-nowrap">
                  Total Deposits Made:
                </span>
                <span className="font-bold text-gray-800 text-xs md:text-sm">
                  ${totalDeposits}
                </span>
                <span
                  className="ml-1 text-[#888888]"
                  title="Sum of all the deposits made for services"
                >
                  {/* info icon */}
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.2 12H8.8V7.2H7.2V12ZM8 5.6C8.22667 5.6 8.41667 5.52333 8.57 5.37C8.72333 5.21667 8.8 5.02667 8.8 4.8C8.8 4.57333 8.72333 4.38333 8.57 4.23C8.41667 4.07667 8.22667 4 8 4C7.77333 4 7.58333 4.07667 7.43 4.23C7.27667 4.38333 7.2 4.57333 7.2 4.8C7.2 5.02667 7.27667 5.21667 7.43 5.37C7.58333 5.52333 7.77333 5.6 8 5.6ZM8 16C6.89333 16 5.85333 15.79 4.88 15.37C3.90667 14.95 3.06 14.38 2.34 13.66C1.62 12.94 1.05 12.0933 0.63 11.12C0.21 10.1467 0 9.10667 0 8C0 6.89333 0.21 5.85333 0.63 4.88C1.05 3.90667 1.62 3.06 2.34 2.34C3.06 1.62 3.90667 1.05 4.88 0.63C5.85333 0.21 6.89333 0 8 0C9.10667 0 10.1467 0.21 11.12 0.63C12.0933 1.05 12.94 1.62 13.66 2.34C14.38 3.06 14.95 3.90667 15.37 4.88C15.79 5.85333 16 6.89333 16 8C16 9.10667 15.79 10.1467 15.37 11.12C14.95 12.0933 14.38 12.94 13.66 13.66C12.94 14.38 12.0933 14.95 11.12 15.37C10.1467 15.79 9.10667 16 8 16ZM8 14.4C9.78667 14.4 11.3 13.78 12.54 12.54C13.78 11.3 14.4 9.78667 14.4 8C14.4 6.21333 13.78 4.7 12.54 3.46C11.3 2.22 9.78667 1.6 8 1.6C6.21333 1.6 4.7 2.22 3.46 3.46C2.22 4.7 1.6 6.21333 1.6 8C1.6 9.78667 2.22 11.3 3.46 12.54C4.7 13.78 6.21333 14.4 8 14.4Z"
                      fill="#888888"
                    />
                  </svg>
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="bg-white px-3 md:px-4 py-2 rounded-md shadow-sm flex items-center justify-between flex-1">
                <span className="text-gray-700 font-medium text-xs md:text-sm whitespace-nowrap">
                  Total Amount To Be Paid:
                </span>
                <div
                  className="flex items-center gap-2 ml-2"
                  title="Total of all deposits and advance payments collected for this order"
                >
                  <span className="font-bold text-gray-900 text-sm md:text-base">
                    ${totalAmountToBeCollected}
                  </span>
                  <span className="text-[#888888]">
                    {/* info icon */}
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7.2 12H8.8V7.2H7.2V12ZM8 5.6C8.22667 5.6 8.41667 5.52333 8.57 5.37C8.72333 5.21667 8.8 5.02667 8.8 4.8C8.8 4.57333 8.72333 4.38333 8.57 4.23C8.41667 4.07667 8.22667 4 8 4C7.77333 4 7.58333 4.07667 7.43 4.23C7.27667 4.38333 7.2 4.57333 7.2 4.8C7.2 5.02667 7.27667 5.21667 7.43 5.37C7.58333 5.52333 7.77333 5.6 8 5.6ZM8 16C6.89333 16 5.85333 15.79 4.88 15.37C3.90667 14.95 3.06 14.38 2.34 13.66C1.62 12.94 1.05 12.0933 0.63 11.12C0.21 10.1467 0 9.10667 0 8C0 6.89333 0.21 5.85333 0.63 4.88C1.05 3.90667 1.62 3.06 2.34 2.34C3.06 1.62 3.90667 1.05 4.88 0.63C5.85333 0.21 6.89333 0 8 0C9.10667 0 10.1467 0.21 11.12 0.63C12.0933 1.05 12.94 1.62 13.66 2.34C14.38 3.06 14.95 3.90667 15.37 4.88C15.79 5.85333 16 6.89333 16 8C16 9.10667 15.79 10.1467 15.37 11.12C14.95 12.0933 14.38 12.94 13.66 13.66C12.94 14.38 12.0933 14.95 11.12 15.37C10.1467 15.79 9.10667 16 8 16ZM8 14.4C9.78667 14.4 11.3 13.78 12.54 12.54C13.78 11.3 14.4 9.78667 14.4 8C14.4 6.21333 13.78 4.7 12.54 3.46C11.3 2.22 9.78667 1.6 8 1.6C6.21333 1.6 4.7 2.22 3.46 3.46C2.22 4.7 1.6 6.21333 1.6 8C1.6 9.78667 2.22 11.3 3.46 12.54C4.7 13.78 6.21333 14.4 8 14.4Z"
                        fill="#888888"
                      />
                    </svg>
                  </span>
                </div>
              </div>

              {/* Estimated Date */}
              <div className="flex items-center gap-2text-[#000A1699] shrink-0 ">
                <span className="text-xs md:text-sm whitespace-nowrap">
                  Estimated completion date:
                </span>
                {isEditOrder ? (
                  <span className="font-semibold text-[#43474E] py-2 px-3 min-w-[100px] inline-block text-center">
                    {dayjs(orderDetails?.orderInfo.estCompletionDate).format(
                      'MM/DD/YYYY',
                    )}
                  </span>
                ) : (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      disablePast
                      value={estCompletionDate}
                      onChange={(newValue) => setEstCompletionDate(newValue)}
                      desktopModeMediaQuery="@media (min-width: 0px)"
                      slots={{ textField: TextField }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: {
                            '& .MuiInputBase-root': {
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              height: '32px',
                              paddingY: '0px',
                            },
                            '& .MuiInputBase-input': {
                              paddingY: '4px',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              backgroundColor: 'white',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#D1D5DB',
                            },
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'white',
                              height: '32px',
                              paddingY: '0px',
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartLayout;
